/**
 * ChatProvider - the island's one chat.
 *
 * It owns the chat's state (`chatReducer`) and its side of the socket
 * protocol (shared/src/schemas/chat.schema.ts). Nothing else here makes a
 * decision: the listeners, registered once per socket, only dispatch what the
 * server pushed; the actions send a request and dispatch its answer. What the
 * state becomes is the reducer's business alone.
 *
 * Requests go out with an acknowledgement and are answered exactly once. A
 * failure stays in the chat - a message marked failed with a retry, a channel
 * that could not be loaded, a notice - and never reaches the room connection's
 * `error` banner, which the server no longer uses for chat at all.
 *
 * After every island join the server sends `room_state` (then
 * `chat:initial_state`), including when the socket re-joins after a reconnect
 * (#115). A reconnected socket starts with no channel subscriptions, so the
 * channel on screen is joined again on `room_state` - never on `connect`, when
 * the socket is not on the island yet and the join would be refused (#116).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import type { Socket } from 'socket.io-client';
import type {
  ChatAck,
  ChatActivityPayload,
  ChatInitialStatePayload,
  ChatMessageEventPayload,
  ChatRequestEvent,
  ChatRequests
} from '@shared/schemas/chat.schema.js';
import { useAppContext } from '../hooks/useAppContext';
import type { ClientChatMessage } from '../types/chat.types';
import { chimeForChatActivity } from '../utils/notification-sound';
import { ChatContext, type ChatContextValue } from './chatContext';
import {
  chatReducer,
  initialChatState,
  isViewing,
  selectActiveChannel,
  selectActiveHistory,
  selectTotalUnread
} from './chatReducer';

/** How long a request waits for its answer before it counts as failed. */
const REQUEST_TIMEOUT_MS = 10_000;

const NOT_CONNECTED: ChatAck<never> = {
  ok: false,
  error: { code: 'CHAT_ERROR', message: 'You are not connected to the island' }
};

const NO_ANSWER: ChatAck<never> = {
  ok: false,
  error: { code: 'CHAT_ERROR', message: 'The chat did not answer. Try again.' }
};

/**
 * Send a chat request and wait for its answer. A socket that is not connected
 * is not asked at all: Socket.IO would buffer the request and send it on
 * reconnect, before the island is joined again, where it would be refused.
 */
function request<E extends ChatRequestEvent>(
  socket: Socket,
  event: E,
  payload: ChatRequests[E]['request']
): Promise<ChatAck<ChatRequests[E]['reply']>> {
  if (!socket.connected) {
    return Promise.resolve(NOT_CONNECTED);
  }
  return new Promise((resolve) => {
    socket
      .timeout(REQUEST_TIMEOUT_MS)
      .emit(event, payload, (timedOut: Error | null, answer: ChatAck<ChatRequests[E]['reply']>) => {
        resolve(timedOut ? NO_ANSWER : answer);
      });
  });
}

/** Stop viewing a channel. Leaving is never refused, so nothing waits for it. */
function leave(socket: Socket, channelId: string): void {
  if (socket.connected) {
    socket.emit('chat:leave_channel', { channelId } satisfies ChatRequests['chat:leave_channel']['request']);
  }
}

// The last channel open on each island is all the chat keeps in the browser.
const lastChannelKey = (roomId: string) => `chat_last_channel_${roomId}`;

function readLastChannel(roomId: string): string | null {
  try {
    return localStorage.getItem(lastChannelKey(roomId));
  } catch {
    return null;
  }
}

function saveLastChannel(roomId: string, channelId: string): void {
  try {
    localStorage.setItem(lastChannelKey(roomId), channelId);
  } catch {
    // Storage unavailable: the drawer just won't reopen on this channel
  }
}

function newTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const { auth, roomConnection } = useAppContext();
  const socket = roomConnection.socket;
  const roomId = roomConnection.currentRoom?.id ?? null;
  const [state, dispatch] = useReducer(chatReducer, initialChatState);

  // What the actions and listeners read when they run, not when they were
  // made: an answer arrives long after its request, and must be judged
  // against the chat as it is then.
  // The user's own status, for the new-message chime (silent in Do Not Disturb)
  const userStatus = roomConnection.playersData?.find((p) => p.userId === auth.user?.id)?.status;
  const latest = useRef({ state, socket, roomId, user: auth.user, userStatus });
  useLayoutEffect(() => {
    latest.current = { state, socket, roomId, user: auth.user, userStatus };
  });

  const join = useCallback((channelId: string) => {
    const { socket } = latest.current;
    if (!socket) {
      dispatch({ type: 'joinFailed', channelId });
      return;
    }
    void request(socket, 'chat:join_channel', { channelId }).then((answer) => {
      if (latest.current.socket !== socket) return; // another island's answer
      const now = latest.current;
      if (answer.ok) {
        dispatch({ type: 'joinSucceeded', view: answer.data, selfId: now.user?.id ?? '' });
      } else {
        dispatch({ type: 'joinFailed', channelId });
      }
      // Answered after the user moved on: the join subscribed the socket to a
      // channel nobody is looking at, so leave it again.
      if (!isViewing(now.state, channelId)) {
        leave(socket, channelId);
      }
    });
  }, []);

  /** Join the channel on screen again, keeping what is shown of it meanwhile. */
  const rejoinActive = useCallback(() => {
    const { state } = latest.current;
    if (state.isOpen && state.activeChannelId) {
      dispatch({ type: 'joinRequested', channelId: state.activeChannelId });
      join(state.activeChannelId);
    }
  }, [join]);

  // Pushes, and the island join. Registered once per socket; they only dispatch.
  useEffect(() => {
    if (!socket) return;

    const onInitialState = (payload: ChatInitialStatePayload) => dispatch({ type: 'initialState', payload });
    const onMessage = ({ channelId, message }: ChatMessageEventPayload) =>
      dispatch({ type: 'messageReceived', channelId, message });
    const onActivity = ({ channel, mentionsYou }: ChatActivityPayload) => {
      dispatch({ type: 'activity', channel });
      // One push per message from someone else: chime for a DM or an @-mention
      const { state, userStatus } = latest.current;
      chimeForChatActivity({
        isDm: channel.type === 'dm',
        mentionsYou: mentionsYou === true,
        onScreen: isViewing(state, channel.id),
        userStatus
      });
    };

    socket.on('chat:initial_state', onInitialState);
    socket.on('chat:message', onMessage);
    socket.on('chat:activity', onActivity);
    socket.on('room_state', rejoinActive);

    return () => {
      socket.off('chat:initial_state', onInitialState);
      socket.off('chat:message', onMessage);
      socket.off('chat:activity', onActivity);
      socket.off('room_state', rejoinActive);
      // Another island, or none: nothing of this one's chat carries over
      dispatch({ type: 'reset' });
    };
  }, [socket, rejoinActive]);

  const open = useCallback(() => {
    const { state, roomId } = latest.current;
    if (state.isOpen) return;
    const saved = roomId ? readLastChannel(roomId) : null;
    const channelId =
      state.activeChannelId ?? (saved && state.channels.some((channel) => channel.id === saved) ? saved : null);
    dispatch({ type: 'drawerOpened', channelId });
    if (channelId) {
      join(channelId);
    }
  }, [join]);

  const close = useCallback(() => {
    const { state, socket } = latest.current;
    if (!state.isOpen) return;
    dispatch({ type: 'drawerClosed' });
    if (socket && state.activeChannelId) {
      leave(socket, state.activeChannelId);
    }
  }, []);

  const toggle = useCallback(() => {
    if (latest.current.state.isOpen) {
      close();
    } else {
      open();
    }
  }, [open, close]);

  const selectChannel = useCallback(
    (channelId: string) => {
      const { state, socket, roomId } = latest.current;
      if (isViewing(state, channelId)) return;
      dispatch({ type: 'channelSelected', channelId });
      if (socket && state.isOpen && state.activeChannelId && state.activeChannelId !== channelId) {
        leave(socket, state.activeChannelId);
      }
      if (roomId) {
        saveLastChannel(roomId, channelId);
      }
      join(channelId);
    },
    [join]
  );

  const startDm = useCallback((targetUserId: string) => {
    const { socket } = latest.current;
    if (!socket) return;
    void request(socket, 'chat:start_dm', { targetUserId }).then((answer) => {
      if (latest.current.socket !== socket) return; // another island's answer
      if (!answer.ok) {
        dispatch({ type: 'noticeShown', message: answer.error.message });
        return;
      }
      const { state, roomId, user } = latest.current;
      const dmId = answer.data.channel.id;
      dispatch({ type: 'dmStarted', reply: answer.data, selfId: user?.id ?? '' });
      if (!state.isOpen) {
        leave(socket, dmId);
        return;
      }
      if (state.activeChannelId && state.activeChannelId !== dmId) {
        leave(socket, state.activeChannelId);
      }
      if (roomId) {
        saveLastChannel(roomId, dmId);
      }
    });
  }, []);

  /** Send our message `tempId` and settle its copy with the answer. */
  const deliver = useCallback((channelId: string, tempId: string, content: string) => {
    const { socket } = latest.current;
    if (!socket) {
      dispatch({ type: 'sendFailed', channelId, tempId });
      return;
    }
    void request(socket, 'chat:send_message', { channelId, content }).then((answer) => {
      if (latest.current.socket !== socket) return; // another island's answer
      dispatch(
        answer.ok
          ? { type: 'sendSucceeded', channelId, tempId, message: answer.data }
          : { type: 'sendFailed', channelId, tempId }
      );
    });
  }, []);

  const sendMessage = useCallback(
    (content: string) => {
      const { state, user } = latest.current;
      const channelId = state.activeChannelId;
      const text = content.trim();
      if (!channelId || !user || !text) return;

      const message: ClientChatMessage = {
        id: newTempId(),
        userId: user.id,
        username: user.username,
        avatar: user.avatar || 'premade_01',
        content: text,
        createdAt: new Date().toISOString(),
        status: 'sending'
      };
      dispatch({ type: 'messageSent', channelId, message });
      deliver(channelId, message.id, text);
    },
    [deliver]
  );

  const retryMessage = useCallback(
    (messageId: string) => {
      const { state } = latest.current;
      const channelId = state.activeChannelId;
      const failed = channelId
        ? state.histories[channelId]?.messages.find((message) => message.id === messageId && message.status === 'failed')
        : undefined;
      if (!channelId || !failed) return;
      dispatch({ type: 'sendRetried', channelId, tempId: messageId, createdAt: new Date().toISOString() });
      deliver(channelId, messageId, failed.content);
    },
    [deliver]
  );

  const loadOlderMessages = useCallback(() => {
    const { state, socket } = latest.current;
    const channelId = state.activeChannelId;
    const history = channelId ? state.histories[channelId] : undefined;
    if (!socket || !channelId || !history?.hasMore || !history.oldestMessageId || history.older === 'loading') {
      return;
    }
    const before = history.oldestMessageId;
    dispatch({ type: 'olderRequested', channelId });
    void request(socket, 'chat:get_messages', { channelId, before }).then((answer) => {
      if (latest.current.socket !== socket) return; // another island's answer
      dispatch(
        answer.ok ? { type: 'olderLoaded', before, page: answer.data } : { type: 'olderFailed', channelId, before }
      );
    });
  }, []);

  const dismissNotice = useCallback(() => dispatch({ type: 'noticeDismissed' }), []);

  const value = useMemo<ChatContextValue>(
    () => ({
      channels: state.channels,
      unreadCounts: state.unreadCounts,
      totalUnread: selectTotalUnread(state),
      isOpen: state.isOpen,
      activeChannel: selectActiveChannel(state),
      history: selectActiveHistory(state),
      notice: state.notice,
      open,
      close,
      toggle,
      selectChannel,
      startDm,
      sendMessage,
      retryMessage,
      retryJoin: rejoinActive,
      loadOlderMessages,
      dismissNotice
    }),
    [
      state,
      open,
      close,
      toggle,
      selectChannel,
      startDm,
      sendMessage,
      retryMessage,
      rejoinActive,
      loadOlderMessages,
      dismissNotice
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
